import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ReportFilterState, toApiParams } from 'src/app/shared/components/report-filter-bar/report-filter-bar.component';

@Component({
  selector: 'app-ecommerce-admin',
  templateUrl: './ecommerce-admin.component.html',
  styleUrls: ['./ecommerce-admin.component.scss']
})
export class EcommerceAdminComponent implements OnInit {

  storeId: string = '';
  storeName: string = '';
  activeTab: string = 'overview';
  storeConfig: any = {};
  dashboardData: any = {};
  dashboardLoading: boolean = false;
  currentDashboardFilter: ReportFilterState = { period: 'this_month' };
  private dashboardLoadSeq = 0;
  isEcommerceEnabled: boolean = false;
  role: string = '';
  orderNumberFilter: string = '';
  storesPage: number = 1;
  storesWord: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ecommerceService: EcommerceService,
    private service: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.role = localStorage.getItem('role') || '';
    this.route.params.subscribe(params => {
      this.storeId = params['id'];
      if (this.storeId) {
        // Verify access for store managers
        this.verifyStoreAccess();
        this.loadStoreConfig();
        // Dashboard load triggered by filter bar in analytics-dashboard child
      }
    });

    // Get active tab from query params; auditors may access orders, basket-audit, and electronic-journal
    this.route.queryParams.subscribe(params => {
      if (this.role === 'receipt_auditor') {
        const allowedTabs = ['orders', 'basket-audit', 'electronic-journal'];
        const requested = params['tab'];
        this.activeTab = allowedTabs.includes(requested) ? requested : 'orders';
      } else {
        const requested = params['tab'];
        this.activeTab = requested || 'overview';
      }
      this.orderNumberFilter = params['orderNumber'] || '';
      this.storesPage = +params['storesPage'] || 1;
      this.storesWord = params['storesWord'] || '';
    });
  }

  loadStoreConfig(): void {
    const role = localStorage.getItem('role');

    // For chain_manager: try to use router state for store name; fall back to store settings endpoint
    if (role === 'chain_manager') {
      const state = history.state;
      if (state && state.storeName) {
        this.storeName = state.storeName;
        // Use store settings endpoint accessible to chain managers
        this.service.getRequest(`store/${this.storeId}/settings`).subscribe(
          (res: any) => {
            const data = res.response?.data || res.data || {};
            this.storeConfig = data;
            this.isEcommerceEnabled = data.isEcommerceEnabled || false;
            this.spinner.hide();
          },
          () => {
            // If settings endpoint also fails, continue with the name we already have
            this.spinner.hide();
          }
        );
      } else {
        // No router state — try store settings endpoint
        this.spinner.show();
        this.service.getRequest(`store/${this.storeId}/settings`).subscribe(
          (res: any) => {
            const data = res.response?.data || res.data || {};
            this.storeConfig = data;
            this.storeName = data.storeName || this.storeId;
            this.isEcommerceEnabled = data.isEcommerceEnabled || false;
            this.spinner.hide();
          },
          (error) => {
            console.error('Failed to load store settings for chain_manager:', error);
            this.spinner.hide();
          }
        );
      }
      return;
    }

    // Default: admin / manager — use existing store endpoint
    this.spinner.show();
    this.service.getRequest(`${ApiUrl.storeById}${this.storeId}`).subscribe(
      (res: any) => {
        if (res.response?.data) {
          const storeData = res.response.data;
          this.storeConfig = storeData;
          this.storeName = storeData.storeName;
          this.isEcommerceEnabled = storeData.isEcommerceEnabled || false;
        }
        this.spinner.hide();
      },
      (error) => {
        console.error('Failed to load store config:', error);
        this.toastr.error('Failed to load store configuration');
        this.spinner.hide();
      }
    );
  }

  loadDashboardData(filter: ReportFilterState = { period: 'this_month' }): void {
    const seq = ++this.dashboardLoadSeq;
    this.dashboardLoading = true;
    const params = toApiParams(filter, { compare: 'true' });

    this.ecommerceService.getStoreDashboard(this.storeId, params).subscribe({
      next: (res: any) => {
        if (seq !== this.dashboardLoadSeq) return;
        if (res.code === 200 && res.data) {
          this.dashboardData = res.data;
        } else if (res.response && res.response.success && res.response.data) {
          this.dashboardData = res.response.data;
        }
        this.dashboardLoading = false;
      },
      error: (error) => {
        if (seq !== this.dashboardLoadSeq) return;
        console.error('Failed to load ecommerce admin dashboard data:', error);
        this.dashboardLoading = false;
        this.toastr.error(error?.error?.response?.message || 'Failed to load dashboard data');
      }
    });
  }

  onDashboardFilterChange(filter: ReportFilterState): void {
    this.currentDashboardFilter = filter;
    this.loadDashboardData(filter);
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab },
      queryParamsHandling: 'merge'
    });
  }

  toggleEcommerce(): void {
    this.spinner.show();
    const newStatus = !this.isEcommerceEnabled;
    
    // Create FormData to match the existing store update API format
    const formData = new FormData();
    formData.append('isEcommerceEnabled', newStatus.toString());
    
    this.service.putRequest(`${ApiUrl.storeUpdateById}/${this.storeId}`, formData).subscribe(
      (res: any) => {
        if (res.response) {
          this.isEcommerceEnabled = newStatus;
          this.toastr.success(
            newStatus ? 'Ecommerce enabled successfully' : 'Ecommerce disabled successfully'
          );
          this.loadStoreConfig();
        }
        this.spinner.hide();
      },
      (error) => {
        console.error('Failed to toggle ecommerce:', error);
        this.toastr.error('Failed to update ecommerce status');
        this.spinner.hide();
      }
    );
  }

  get breeceEnabled(): boolean {
    return !!this.storeConfig?.breeceConfig?.enabled;
  }

  goBackToStores(): void {
    this.router.navigate(['/stores'], {
      queryParams: {
        page: this.storesPage > 1 ? this.storesPage : null,
        word: this.storesWord || null,
      },
    });
  }

  goBackToAuditor(): void {
    this.router.navigate(['/auditor-orders']);
  }

  refreshData(): void {
    this.loadStoreConfig();
    this.loadDashboardData(this.currentDashboardFilter);
  }

  private verifyStoreAccess(): void {
    const role = localStorage.getItem('role');
    const managerId = localStorage.getItem('managerId');

    // If user is a manager, verify they have access to this store
    if (role === 'manager' && managerId) {
      // For now, we'll allow access and let the backend verify permissions
      // The API calls will return 403 if the manager doesn't have access to this store
      console.log('Store manager accessing store:', this.storeId);
    }
  }
}